// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useRef } from 'react';

export interface AutosaveOptions {
  /** Truthy when there are unsaved changes. */
  dirty: boolean;
  /** Debounce in ms. PLAN.md §8 Q8: 5000 (5s). */
  delayMs: number;
  /** Save callback. Called once per quiet period. */
  onSave: () => void | Promise<void>;
}

/**
 * Debounced autosave hook. Every time `dirty` flips truthy (or changes
 * while still dirty), the timer restarts; once `delayMs` elapses without
 * a new edit, `onSave` is fired. Per PLAN.md §8 decision 8: cadence is
 * "Every 5 seconds when dirty, debounced."
 */
export function useDebouncedAutosave(opts: AutosaveOptions): void {
  const { dirty, delayMs, onSave } = opts;
  const savedRef = useRef(onSave);
  savedRef.current = onSave;
  useEffect(() => {
    if (!dirty) return undefined;
    const handle = setTimeout(() => {
      void savedRef.current();
    }, delayMs);
    return () => {
      clearTimeout(handle);
    };
  }, [dirty, delayMs]);
}
